import React, { useRef, useState } from "react";
import { formatCurrency } from "../../../utils/formatters";
import { QBO_JOB_EXPORT_NOTE, QBO_PASTE_HEADING } from "./traceLabels";

/**
 * One row of the cost trace.
 *
 * A node with something to explain renders as a native <details> disclosure so
 * keyboard and screen-reader behaviour comes free. A node that is only a number
 * (a sub-total inside a parent) renders as a flat line.
 *
 * This component renders descriptors from traceModel.js; it never reads a
 * backend payload itself and never does arithmetic.
 */

function formatValue(value, valueKind) {
  if (value === null || value === undefined || value === "") return null;
  if (valueKind === "currency") return formatCurrency(value);
  if (valueKind === "hours") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? `${Number(parsed.toFixed(4))} hr` : null;
  }
  if (valueKind === "number") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? String(Number(parsed.toFixed(4))) : null;
  }
  return String(value);
}

function isExpandable(node) {
  return Boolean(
    (node.formula && node.formula.length) ||
    (node.rows && node.rows.length) ||
    node.table ||
    (node.source && node.source.length) ||
    (node.warnings && node.warnings.length) ||
    (node.children && node.children.length) ||
    (node.actions && node.actions.length) ||
    node.note ||
    node.qbo ||
    node.secondary,
  );
}

function Warnings({ warnings = [] }) {
  if (!warnings.length) return null;
  return (
    <>
      {warnings.map((warning) => (
        <div key={warning.text}>
          <p
            className={`wb-trace__warn wb-trace__warn--${warning.tone || "warn"}`}
            role={warning.tone === "error" ? "alert" : "status"}
          >
            {warning.text}
          </p>
          {/* The machine-shaped payload behind a human sentence — available for
              a bug report, never the thing the estimator has to read. */}
          {warning.detail ? (
            <details className="wb-trace__detail">
              <summary>Technical details</summary>
              <code>{warning.detail}</code>
            </details>
          ) : null}
        </div>
      ))}
    </>
  );
}

/**
 * A value with a Copy button — the QuickBooks description and unit price.
 *
 * The failure case is the point. The clipboard API is unavailable on a plain
 * HTTP LAN deployment and can be refused by permissions policy anywhere, and a
 * copy that silently does nothing leaves whatever was in the clipboard before
 * sitting there — so the estimator pastes the LAST line's price into
 * QuickBooks and never learns otherwise. So a failure says so, and selects the
 * value on screen so Cmd/Ctrl-C still gets the job done.
 */
function CopyField({
  label,
  text,
  copyValue,
  buttonLabel,
  block = false,
  valueClassName = "",
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const valueRef = useRef(null);

  /** Hand the value to the user directly when we could not hand it to them. */
  const selectValue = () => {
    const node = valueRef.current;
    if (!node || typeof window.getSelection !== "function") return;
    try {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(node);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (error) {
      /* Selection is a courtesy; never let it mask the copy failure. */
    }
  };

  const copy = async () => {
    const value = String(copyValue ?? text ?? "");
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const area = document.createElement("textarea");
        area.value = value;
        area.style.position = "fixed";
        area.style.left = "-9999px";
        document.body.appendChild(area);
        area.select();
        // execCommand reports refusal by RETURNING false, not by throwing.
        // Trusting it blind is how a failed copy used to report "Copied".
        const ok = document.execCommand("copy");
        document.body.removeChild(area);
        if (!ok) throw new Error("execCommand('copy') was refused");
      }
      setCopyFailed(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch (error) {
      console.error("Unable to copy text:", error);
      setCopied(false);
      setCopyFailed(true);
      selectValue();
      // Longer than the success flash: this one has to be read, not glanced at.
      window.setTimeout(() => setCopyFailed(false), 2500);
    }
  };

  return (
    <div className={`wb-trace__copy${block ? " wb-trace__copy--block" : ""}`}>
      <span className="wb-trace__copy-k">{label}</span>
      <span
        className={`wb-trace__copy-v ${valueClassName}`.trim()}
        ref={valueRef}
      >
        {text}
      </span>
      <button
        type="button"
        className={`wb-trace__mini${copyFailed ? " wb-trace__mini--failed" : ""}${
          copied ? " wb-trace__mini--copied" : ""
        }`}
        // A button whose own words say what it copies needs no second name.
        aria-label={buttonLabel ? undefined : `Copy ${label}`}
        // The label IS the outcome, so the change has to be announced.
        aria-live="polite"
        onClick={copy}
      >
        {copied
          ? "Copied"
          : copyFailed
            ? "Couldn't copy"
            : buttonLabel || "Copy"}
      </button>
    </div>
  );
}

/* The three renderers the secondary bucket reuses. Lifted out of TraceBody
   verbatim so a node can show its rows above the fold and defer its
   provenance behind a click without the markup existing twice. */

function TraceRows({ node, rows }) {
  return (
    <>
      {(rows || []).map((line) => (
        <div className="wb-trace__line" key={`${node.id}-${line.label}`}>
          <span>{line.label}</span>
          <b className="wb-num">
            {formatValue(line.value, line.valueKind)}
            {line.valueSuffix ? (
              <em className="wb-trace__suffix"> {line.valueSuffix}</em>
            ) : null}
          </b>
        </div>
      ))}
    </>
  );
}

function TraceTable({ table }) {
  if (!table) return null;
  return (
    <div className="wb-trace__table-wrap">
      <table className="wb-trace__table">
        <thead>
          <tr>
            {table.head.map((heading) => (
              <th key={heading} scope="col">
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((cells) => (
            <tr key={cells[0]}>
              {cells.map((cell, cellIndex) =>
                cellIndex === 0 ? (
                  <th key={cell} scope="row">
                    {cell}
                  </th>
                ) : (
                  <td key={`${cells[0]}-${cellIndex}`} className="wb-num">
                    {cell}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TraceSource({ node, source }) {
  return (
    <>
      {(source || []).map((leaf) => (
        <div className="wb-trace__leaf" key={`${node.id}-${leaf.label}`}>
          <span className="wb-trace__src">
            {leaf.label}
            <b>{formatValue(leaf.value, leaf.valueKind || "text")}</b>
            {leaf.hint ? <i>{leaf.hint}</i> : null}
          </span>
        </div>
      ))}
    </>
  );
}

/* A secondary bucket with nothing in it must not draw a summary row. */
function hasSecondary(secondary) {
  if (!secondary) return false;
  return Boolean(
    (secondary.rows || []).length ||
    secondary.table ||
    (secondary.source || []).length,
  );
}

/**
 * React key for a trace node. A node carrying an error remounts per line, so
 * it opens from its own props (TraceNode's hasError rule) even if the
 * estimator closed the same node on the previous line -- an unchanged `open`
 * prop is never re-written to the DOM. Applied at every depth.
 */
export function traceNodeKey(node, lineKey) {
  const hasError = (node?.warnings || []).some(
    (warning) => warning?.tone === "error",
  );
  return hasError && lineKey ? `${node.id}@${lineKey}` : node.id;
}

function TraceBody({
  node,
  depth,
  onAction,
  openState,
  onOpenChange,
  lineKey,
}) {
  return (
    <div className="wb-trace__body">
      {node.note ? <p className="wb-trace__note">{node.note}</p> : null}

      {(node.formula || []).map((sentence) => (
        <p key={sentence} className="wb-trace__formula">
          {sentence}
        </p>
      ))}

      <TraceRows node={node} rows={node.rows} />

      <TraceTable table={node.table} />

      <Warnings warnings={node.warnings} />

      <TraceSource node={node} source={node.source} />

      {node.qbo ? (
        <div className="wb-trace__qbo">
          {node.qbo.tsv ? (
            /* The whole job in one paste. The rows are shown, not just
               copied: they are what lands in QuickBooks, and after a refused
               copy they are what the estimator selects by hand. */
            <>
              <p className="wb-trace__note">{QBO_JOB_EXPORT_NOTE}</p>
              <CopyField
                block
                label="QuickBooks rows"
                text={node.qbo.tsv}
                valueClassName="wb-trace__tsv"
                buttonLabel={`Copy all ${node.qbo.lineCount} ${
                  node.qbo.lineCount === 1 ? "line" : "lines"
                } (description · qty · unit price)`}
              />
            </>
          ) : (
            /* Pasting into QuickBooks is an errand done once per job, at the
               end. The heading is the summary now — one click when the
               estimator is actually ready to do it. */
            <details className="wb-trace__more wb-trace__qbo-more">
              <summary>{QBO_PASTE_HEADING}</summary>
              <CopyField
                label="QuickBooks description"
                text={node.qbo.description}
              />
              <CopyField
                label="QuickBooks unit price"
                text={formatCurrency(node.qbo.unitPrice)}
                copyValue={node.qbo.unitPrice}
              />
            </details>
          )}
        </div>
      ) : null}

      {(node.actions || []).length ? (
        <div className="wb-trace__actions">
          {node.actions.map((action) => (
            <button
              key={action.id}
              type="button"
              className="wb-trace__mini"
              title={action.hint || undefined}
              onClick={() => onAction && onAction(action.id, node)}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}

      {/* Provenance behind one click. The number and its formula stay above;
          this is the "prove it" drawer — the record, the sheet price, the
          rate table. */}
      {hasSecondary(node.secondary) ? (
        <details className="wb-trace__more">
          <summary>{node.secondary.label || "Where this came from"}</summary>
          <TraceRows node={node} rows={node.secondary.rows} />
          <TraceTable table={node.secondary.table} />
          <TraceSource node={node} source={node.secondary.source} />
        </details>
      ) : null}

      {(node.children || []).map((child) => (
        <TraceNode
          key={traceNodeKey(child, lineKey)}
          node={child}
          depth={depth + 1}
          onAction={onAction}
          openState={openState}
          onOpenChange={onOpenChange}
          lineKey={lineKey}
        />
      ))}
    </div>
  );
}

function TraceNode({
  node,
  depth = 0,
  onAction,
  openState,
  onOpenChange,
  lineKey = null,
}) {
  if (!node) return null;

  /* The engine strip: how this line was priced, before any dollar figure. */
  /* Only ever a fallback warning now — the engine and geometry names are the
     editor's subtitle, one pane to the left. */
  if (node.kind === "provenance") {
    return (
      <div className="wb-trace__provenance">
        <Warnings warnings={node.warnings} />
      </div>
    );
  }

  /* Pass-through carve-out: reported separately, still fully marked up. */
  if (node.kind === "note") {
    return (
      <div className="wb-trace__carve">
        <div className="wb-trace__carve-hd">
          <span>{node.label}</span>
          <b className="wb-num">{formatValue(node.value, node.valueKind)}</b>
        </div>
        <p>{node.note}</p>
      </div>
    );
  }

  const value = formatValue(node.value, node.valueKind);

  if (!isExpandable(node)) {
    return (
      <div className="wb-trace__flat" data-depth={depth}>
        <span className="wb-trace__flat-k">{node.label}</span>
        <b className="wb-num">{value}</b>
      </div>
    );
  }

  /* A node the estimator opened or closed by hand outranks the descriptor's
     default — but only that node, and only until the intent is recorded.
     Leaving `open` an unmanaged prop is what let one line's collapse leak
     onto the next: React never rewrites an attribute whose prop did not
     change, so the DOM kept the previous line's state by accident. */
  const userOpen =
    openState && Object.prototype.hasOwnProperty.call(openState, node.id)
      ? openState[node.id]
      : null;
  // An error never hides behind a node the estimator closed on another line:
  // the closed state is remembered per node id for the whole session.
  const hasError = (node.warnings || []).some(
    (warning) => warning?.tone === "error",
  );
  const isOpen = hasError
    ? true
    : userOpen === null
      ? Boolean(node.defaultOpen)
      : userOpen;

  return (
    <details
      className="wb-node"
      data-tone={node.tone || "neutral"}
      data-depth={depth}
      open={isOpen}
      onToggle={(event) =>
        onOpenChange && onOpenChange(node.id, event.currentTarget.open)
      }
    >
      <summary>
        <span className="wb-node__tw" aria-hidden="true">
          ▶
        </span>
        <span className="wb-node__label">
          {node.tone && node.tone !== "neutral" ? (
            <i
              className="wb-node__dot"
              data-tone={node.tone}
              aria-hidden="true"
            />
          ) : null}
          {node.label}
          {(node.badges || []).map((badge) => (
            <span
              key={badge.text}
              className={`wb-node__badge wb-node__badge--${badge.tone || "warn"}`}
            >
              {badge.text}
            </span>
          ))}
        </span>
        <span className="wb-node__val wb-num">
          {value}
          {node.valueSuffix ? (
            <em className="wb-trace__suffix"> {node.valueSuffix}</em>
          ) : null}
        </span>
      </summary>
      <TraceBody
        node={node}
        depth={depth}
        onAction={onAction}
        openState={openState}
        onOpenChange={onOpenChange}
        lineKey={lineKey}
      />
    </details>
  );
}

export default TraceNode;
